import { JSX, ReactNode, useEffect, useMemo, useState } from "react";
import { Button, CheckboxStateValues, FileUploadProvider, Icon, Text } from "@ucl/react-library";
import { Trans, useTranslation } from "react-i18next";
import { FormProvider, Path, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AnyObjectSchema } from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useCase } from "src/context/case-context";
import DocumentUpload from "src/components/document-upload/document-upload";
import { ChallengeReason } from "src/components/challenge-reason/challenge-reason";
import { ChallengeFormValues } from "src/types/challenge-form-types";
import { ContactDetailsEdit } from "src/components/contact-details/contact-details-edit";
import { ContactDetailsView } from "src/components/contact-details/contact-details-view";
import { CustomerResponse } from "src/api/types/responses";
import { ConfirmDocuments } from "src/components/confirm-documents/confirm-documents";
import { ApiError } from "src/api/api-error";
import { uploadMfcDocuments } from "src/api/services/document-service";
import { InlineAlertMessage } from "src/components/inline-alert-message/inline-alert-message";
import {
  createContactDetailsSchema,
  createContactPreferenceSchema,
  getContactDetailsDefaults,
  OfferStatus,
} from "src/pages/case-landing/case-form-setup";
import { createChallengeDetailsSchema } from "./challenge-schema";
import { updateCustomerDetails } from "src/api/services/customer-service";
import { mapCustomerUpdateFormData } from "src/utils/map-customer-update-data";
import { convertDocumentsToPdf } from "src/utils/docUploads/convert-documents-to-pdf";
import { translationMapping } from "src/utils/translation-mappings";
import { Loader } from "src/utils/loader";

const TOTAL_STEPS = 4;

type StepConfig = {
  step: number;
  titleKey: string;
  body: ReactNode;
};

type ChallengePageContentProps = {
  customerData: CustomerResponse;
  setCustomerData: (data: CustomerResponse) => void;
};

function ChallengePageContent({ customerData, setCustomerData }: ChallengePageContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showEditDetails, setShowEditDetails] = useState(false);
  const [documentUploaderKey, setDocumentUploaderKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConvertingDocuments, setIsConvertingDocuments] = useState(false);

  const [showBackendError, setShowBackendError] = useState(false);
  const [backendErrorMessage, setBackendErrorMessage] = useState<{
    title: string;
    body: ReactNode;
  }>({
    title: "",
    body: <></>,
  });

  const currentStepParam = Number(searchParams.get("step") ?? 1);

  const currentStep = currentStepParam >= 1 && currentStepParam <= TOTAL_STEPS ? currentStepParam : 1;

  const contactDetailsSchema = useMemo(() => createContactDetailsSchema(t), [t]);

  const contactPreferenceSchema = useMemo(() => createContactPreferenceSchema(t), [t]);

  const challengeDetailsSchema = useMemo(() => createChallengeDetailsSchema(t), [t]);

  const contactDetailsDefaults = useMemo(() => getContactDetailsDefaults(customerData), [customerData]);

  const challengeFormSchema: AnyObjectSchema = useMemo(() => {
    return challengeDetailsSchema.concat(contactDetailsSchema).concat(contactPreferenceSchema);
  }, [contactDetailsSchema, contactPreferenceSchema, challengeDetailsSchema]);

  const validationContext = useMemo(
    () => ({
      requireDocumentTypes: currentStep === 4,
    }),
    [currentStep]
  );

  const methods = useForm<ChallengeFormValues>({
    defaultValues: {
      ...contactDetailsDefaults,
      preferredContactChannel: customerData.preferredContactChannel ?? undefined,
      challengeReason: "",
      challengeExplanation: "",
      documents: [],
      agreeTsAndCs: CheckboxStateValues.UNCHECKED,
    },
    resolver: yupResolver(challengeFormSchema),
    context: validationContext,
    mode: "all",
    reValidateMode: "onChange",
    shouldUnregister: false,
  });

  const documents = methods.watch("documents") ?? [];
  const hasUploadedDocuments = documents.length > 0;
  const docErrors = methods.formState.errors.documents;
  const hasNumOfFilesError = docErrors?.type === "string" || (docErrors?.type === "manual" && docErrors.message === "numOfFiles");

  const checked = methods.watch("agreeTsAndCs");
  const isAgreed = checked === CheckboxStateValues.CHECKED;

  const steps = useMemo<StepConfig[]>(
    () => [
      {
        step: 1,
        titleKey: "challenge.steps.contactDetails.title",
        body: (
          <div data-testid="challengeContactDetails" className="flex w-full flex-col gap-6">
            <div className="flex flex-col gap-4">
              <Text.P size="large">{t("challenge.steps.contactDetails.checkDetails")}</Text.P>

              <Text.P size="large">{t("challenge.steps.contactDetails.updateAll")}</Text.P>

              <Text.P size="large">
                <Trans i18nKey="challenge.steps.contactDetails.currentAgreement" components={translationMapping(t)} />
              </Text.P>
              <Text.P size="large">{t("challenge.steps.contactDetails.reviewYourChallenge")}</Text.P>

            </div>
            {showEditDetails ? (
              <ContactDetailsEdit showContactPreference={true} />
            ) : (
              <ContactDetailsView
                toggleContactDetails={() => {
                  setShowEditDetails(true);
                  window.scrollTo(0, 0);
                }}
                showContactExplanation={true}
              />
            )}
          </div>
        ),
      },
      {
        step: 2,
        titleKey: "challenge.steps.challengeReason.title",
        body: <ChallengeReason />,
      },
      {
        step: 3,
        titleKey: "challenge.steps.documentUpload.title",
        body: <DocumentUpload resetUploader={() => setDocumentUploaderKey((key) => key + 1)} />,
      },
      {
        step: 4,
        titleKey: hasUploadedDocuments ? "challenge.steps.confirmDocuments.title" : "",
        body: <ConfirmDocuments />,
      },
    ],
    [showEditDetails, hasUploadedDocuments]
  );

  const activeStep = steps[currentStep - 1];

  const clearBackendError = () => {
    setShowBackendError(false);
    setBackendErrorMessage({
      title: "",
      body: <></>,
    });
  };

  const goToStep = (step: number) => {
    clearBackendError();

    setSearchParams({
      step: String(step),
    });
  };

  const contactFieldsToValidate: Path<ChallengeFormValues>[] = [
    "email",
    "homePhone",
    "address.addressLine1",
    "address.addressLine2",
    "address.city",
    "address.postCode",
    "address.country",
    "preferredContactChannel",
  ];

  const validateContactDetailsStep = async () => {

    return methods.trigger(contactFieldsToValidate, {
      shouldFocus: true,
    });
  };

  const validateChallengeReasonStep = async () => {
    return methods.trigger(["challengeReason", "challengeExplanation"], {
      shouldFocus: true,
    });
  };

  const validateDocumentUploadStep = async () => {
    const isValid = await methods.trigger("documents");

    if (!isValid) {
      return false;
    }

    const documentsToConvert = methods.getValues("documents") ?? [];

    if (documentsToConvert.length === 0) {
      return true;
    }

    setIsConvertingDocuments(true);

    try {
      const success = await convertDocumentsToPdf(methods.getValues, methods.setValue, t);

      if (!success) {
        methods.setError("documents", {
          type: "manual",
          message: "conversionFailed",
        });

        setShowBackendError(true);
        setBackendErrorMessage({
          title: t("errors.genericErrorBanner.title"),
          body: t("documentUpload.conversionFailed"),
        });

        return false;
      }

      return true;
    } finally {
      setIsConvertingDocuments(false);
    }
  };

  const validateConfirmDocumentsStep = async () => {
    const documents = methods.getValues("documents") ?? [];

    if (documents.length === 0) {
      methods.clearErrors("documents");
      return true;
    }

    const documentTypeFields = documents.map(
      (_, index) => `documents.${index}.documentType` as Path<ChallengeFormValues>
    );

    return methods.trigger(documentTypeFields, {
      shouldFocus: true,
    });
  };

  const validateCurrentStep = async () => {
    switch (currentStep) {
      case 1:
        return validateContactDetailsStep();

      case 2:
        return validateChallengeReasonStep();

      case 3:
        return validateDocumentUploadStep();

      case 4:
        return validateConfirmDocumentsStep();

      default:
        return true;
    }
  };
  const errors = methods.formState.errors;

  useEffect(() => {
    if (errors.address || errors.preferredContactChannel) {
      setShowEditDetails(true);
    }
  }, [errors.address, errors.preferredContactChannel]);

  const handleContinue = async () => {
    clearBackendError();

    const isStepValid = await validateCurrentStep();

    if (!isStepValid) {
      return;
    }
    goToStep(currentStep + 1);
  };

  const onSubmit = async (formValues: ChallengeFormValues) => {
    const submittedDocuments = formValues.documents ?? [];

    const hasSubmittedDocuments = submittedDocuments.length > 0;

    try {
      setIsSubmitting(true);
      clearBackendError();

      /*
       * Do not call the document-upload endpoint with an empty array.
       */
      if (hasSubmittedDocuments) {
        await uploadMfcDocuments(submittedDocuments);
      }

      const data = {
        redress: {
          docsUploadStatus: hasSubmittedDocuments,
        },
        offerStatus: OfferStatus.FormalObjection,
      };

      const mappedData = mapCustomerUpdateFormData({
        ...formValues,
        ...data,
      });

      const updatedData = await updateCustomerDetails(mappedData);
      setCustomerData(updatedData);

      navigate("/thank-you", {
        state: {
          pageType: "objectionSubmitted",
        },
      });
    } catch (error) {
      let message: string | JSX.Element = t("errors.genericErrorBanner.title");
      if (error instanceof Error) {
        message = error.message;
      }

      methods.setError("root.server", {
        type: "server",
        message,
      });
      if (error instanceof ApiError) {
        message = <Trans i18nKey={"errors.genericErrorBanner.docUploadBody"} components={translationMapping(t)} />;
      }

      setShowBackendError(true);
      setBackendErrorMessage({
        title: t("errors.genericErrorBanner.title"),
        body: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    } else if (currentStep === 1) {
      navigate("/case");
    }
  };

  const getContinueButtonText = () => {
    if (currentStep !== 3) {
      return t("common.continue");
    }

    return hasUploadedDocuments
      ? t("challenge.actions.continueToReview")
      : t("challenge.actions.continueWithoutEvidence");
  };

  return (
    <FormProvider {...methods}>
      <FileUploadProvider key={documentUploaderKey}>
        <div className="flex flex-col items-start gap-10">
          <div className="flex w-full flex-col">
            <div className="flex flex-col gap-2">
              <Text.P size="small" className="text-primary">
                {t("challenge.stepCounter", {
                  current: currentStep,
                  total: TOTAL_STEPS,
                })}
              </Text.P>

              <Text renderAs="h2" styleType="h3" fontWeight="semi-bold">
                {t(activeStep.titleKey)}
              </Text>
            </div>

            <div className="w-full">
              {steps.map(({ step, body }) => (
                <div key={step} hidden={step !== currentStep} className="w-full">
                  {body}
                </div>
              ))}
            </div>

            {showBackendError && (
              <div data-testid="challengeBackendError" className={"pt-5"}>
                <InlineAlertMessage
                  title={backendErrorMessage.title}
                  body={backendErrorMessage.body}
                  variant="error"
                  onClose={clearBackendError}
                />
              </div>
            )}

            <div className="flex w-full flex-col gap-3 pt-6">
              {currentStep === TOTAL_STEPS ? (
                <Button type="submit" onClick={methods.handleSubmit(onSubmit)} disabled={isSubmitting || !isAgreed}>
                  {isSubmitting ? t("common.submitting") : t("challenge.actions.submitFormalObjection")}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={isConvertingDocuments || (currentStep === 3 && hasNumOfFilesError)}
                  data-testid="challengeContinueButton"
                >
                  {isConvertingDocuments ? t("documentUpload.submitting") : getContinueButtonText()}
                </Button>
              )}

              <Button type="button" variant="tertiary" onClick={handleBack}>
                <Icon icon="caretLeft" />
                {t("common.goBack")}
              </Button>
            </div>
          </div>
          {isSubmitting && <Loader />}
        </div>
      </FileUploadProvider>
    </FormProvider>
  );
}

export default function ChallengePage() {
  const { customerData, setCustomerData } = useCase();

  if (!customerData) {
    return null;
  }

  return <ChallengePageContent customerData={customerData} setCustomerData={setCustomerData} />;
}
